/**
 * کتابخانه رسانه
 * ===============
 * یک پنجره برای انتخاب تصویر: یا از میان تصاویر آپلودشده، یا با آپلود تازه.
 *
 * چرا «متن جایگزین» (alt) اینجا و کنار خود فایل ذخیره می‌شود و نه فقط داخل
 * مقاله؟ چون یک تصویر معمولاً در چند مطلب استفاده می‌شود و نوشتن دوباره‌ی
 * توضیح، کاری است که در عمل انجام نمی‌شود — و تصویرِ بدون متن جایگزین هم برای
 * کاربر نابینا بی‌فایده است و هم در جستجوی تصویر گوگل دیده نمی‌شود.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Upload, Search, Trash2, Check, Loader2, ImageOff, X } from 'lucide-react'
import { get, del, patch, qs } from '../lib/api'
import { toast } from '../store'
import { fa, asArray } from '../lib/format'

const BASE = import.meta.env.VITE_API_URL || ''

export type Media = {
  id: number; url: string; path: string; name: string; alt: string
  format: string; size: number; sizeLabel: string; createdAt: string
}

/**
 * آپلود یک فایل.
 *
 * عمداً از `fetch` خام استفاده می‌شود، نه از کمکی `api()`: آن کمکی همیشه
 * `Content-Type: application/json` می‌گذارد، و برای `FormData` مرورگر باید
 * خودش این هدر را با مرز (boundary) بسازد. اگر دستی تنظیم شود، سرور بدنه را
 * نمی‌تواند تجزیه کند.
 */
export async function uploadImage(file: File, alt = ''): Promise<Media & { duplicate: boolean }> {
  const form = new FormData()
  form.append('file', file)
  const token = localStorage.getItem('az_token')
  const res = await fetch(`${BASE}/api/admin/media${qs({ alt })}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  const text = await res.text()
  let data: any = null
  try { data = text ? JSON.parse(text) : null } catch { data = null }
  if (!res.ok) {
    throw new Error((data && (data.detail ?? data.message)) || 'بارگذاری تصویر ناموفق بود')
  }
  return data
}

export default function MediaPicker({
  open, onClose, onPick,
}: {
  open: boolean
  onClose: () => void
  onPick: (url: string, alt: string) => void
}) {
  const [rows, setRows] = useState<Media[] | null>(null)
  const [q, setQ] = useState('')
  const [info, setInfo] = useState<any>({})
  const [busy, setBusy] = useState(false)
  const [drag, setDrag] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const load = useCallback(() => {
    get(`/admin/media${qs({ q, per_page: 40 })}`)
      .then((r: any) => { setRows(asArray<Media>(r, 'data')); setInfo(r) })
      .catch(() => setRows([]))
  }, [q])

  useEffect(() => { if (open) load() }, [open, load])

  // بستن با کلید Esc — انتظار همیشگی کاربر از یک پنجره
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const doUpload = async (files: FileList | File[] | null) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith('image/'))
    if (!list.length) return
    setBusy(true)
    let dupes = 0
    try {
      for (const f of list) {
        const r = await uploadImage(f)
        if (r.duplicate) dupes++
      }
      toast.ok(dupes
        ? `بارگذاری شد (${fa(dupes)} تصویر تکراری بود و همان قبلی استفاده شد)`
        : `${fa(list.length)} تصویر بارگذاری شد`)
      load()
    } catch (e: any) {
      toast.err(e.message)
    } finally {
      setBusy(false)
    }
  }

  const remove = async (m: Media) => {
    try {
      await del(`/admin/media/${m.id}`)
      toast.ok('تصویر حذف شد')
      load()
    } catch (e: any) {
      toast.err(e.message)
    }
  }

  const saveAlt = async (m: Media, alt: string) => {
    if (alt === m.alt) return
    try {
      await patch(`/admin/media/${m.id}`, { alt })
      setRows((cur) => (cur || []).map((r) => (r.id === m.id ? { ...r, alt } : r)))
    } catch (e: any) {
      toast.err(e.message)
    }
  }

  if (!open) return null

  /*
   * مثل `Drawer` در ui.tsx با پورتال رندر می‌شود: این پنجره از داخل فرم
   * مطلب باز می‌شود و آن فرم درون `card` است که `backdrop-filter` دارد؛
   * بدون پورتال، `fixed inset-0` به جای پنجره‌ی مرورگر اندازه‌ی همان کارت
   * می‌شود و کتابخانه تصاویر داخل فرم حبس می‌ماند. توضیح کامل در ui.tsx.
   */
  return createPortal(
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 p-4 backdrop-blur-sm"
         onClick={onClose}>
      {/* dvh و نه vh: روی گوشی، vh نوار آدرس مرورگر را حساب نمی‌کند */}
      <div className="flex max-h-[86dvh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-ink-900 shadow-card"
           onClick={(e) => e.stopPropagation()}>
        {/* سربرگ */}
        <div className="flex items-center gap-3 border-b border-white/[.07] p-4">
          <h3 className="font-bold text-white">کتابخانه تصاویر</h3>
          <span className="text-[11px] text-slate-500">
            {fa(info.usedMb ?? 0)} از {fa(info.maxMb ?? 0)} مگابایت — سقف هر فایل {fa(info.maxFileMb ?? 5)} مگابایت
          </span>
          <button onClick={onClose} className="mr-auto rounded-lg p-1.5 text-slate-400 hover:bg-white/[.06]">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* نوار ابزار */}
        <div className="flex flex-wrap items-center gap-2 border-b border-white/[.07] p-3">
          <div className="relative min-w-[10rem] flex-1">
            <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input className="input !py-2 !pr-9 text-xs" placeholder="جستجو در نام یا توضیح تصویر…"
                   value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <button className="btn-primary !py-2 text-xs" disabled={busy}
                  onClick={() => fileRef.current?.click()}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            بارگذاری تصویر
          </button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/avif"
                 multiple hidden onChange={(e) => { doUpload(e.target.files); e.target.value = '' }} />
        </div>

        {/* شبکه تصاویر */}
        <div
          className={`flex-1 overflow-y-auto p-3 ${drag ? 'ring-2 ring-inset ring-brand-500/60' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDrag(true) }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files) }}
        >
          {rows === null ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="skeleton h-32 rounded-xl" />)}
            </div>
          ) : rows.length === 0 ? (
            <div className="grid place-items-center py-16 text-center">
              <ImageOff className="mb-3 h-8 w-8 text-slate-600" />
              <p className="text-sm text-slate-400">هنوز تصویری بارگذاری نشده است.</p>
              <p className="mt-1 text-[11px] text-slate-600">
                تصویر را همین‌جا رها کنید یا دکمه بارگذاری را بزنید.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {rows.map((m) => (
                <div key={m.id} className="group overflow-hidden rounded-xl border border-white/[.07] bg-white/[.02]">
                  <button type="button" onClick={() => onPick(m.url, m.alt)}
                          className="relative block h-28 w-full overflow-hidden">
                    <img src={m.url} alt={m.alt || m.name} loading="lazy"
                         className="h-full w-full object-cover transition group-hover:scale-105" />
                    <span className="absolute inset-0 grid place-items-center bg-brand-600/70 opacity-0 transition group-hover:opacity-100">
                      <Check className="h-6 w-6 text-white" />
                    </span>
                  </button>
                  <div className="p-2">
                    <input
                      defaultValue={m.alt}
                      placeholder="توضیح تصویر (alt)"
                      onBlur={(e) => saveAlt(m, e.target.value)}
                      className="w-full rounded-md border border-white/[.06] bg-transparent px-2 py-1 text-[11px] text-slate-300 outline-none focus:border-brand-500/40"
                    />
                    <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-600">
                      <span className="num">{m.sizeLabel}</span>
                      <button onClick={() => remove(m)} title="حذف"
                              className="rounded p-1 text-slate-500 transition hover:bg-down/10 hover:text-down">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-white/[.07] px-4 py-2.5 text-[11px] text-slate-600">
          قالب‌های مجاز: JPG، PNG، GIF، WebP، AVIF — فایل SVG به دلایل امنیتی پذیرفته نمی‌شود.
        </div>
      </div>
    </div>,
    document.body,
  )
}
